import React, { useEffect, useRef, useState } from "react";
import { MessageSquareWarning } from "lucide-react";
import { crmApi } from "../../lib/crmApi";

const inputCls = "w-full border border-[#6E1F2C]/25 bg-white px-2 py-1 text-[11px] text-[#1C110C] focus:outline-none focus:border-[#6E1F2C]";

export function ReplyFeedbackButton({ leadId, conversationId }) {
  const [open, setOpen] = useState(false);
  const [tags, setTags] = useState({});
  const [sel, setSel] = useState([]);
  const [comment, setComment] = useState("");
  const [missedServices, setMissedServices] = useState("");
  const [missedValue, setMissedValue] = useState("");
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState("");
  const ref = useRef(null);

  useEffect(() => {
    if (open && Object.keys(tags).length === 0) crmApi.feedbackTags().then((r) => setTags(r.tags)).catch(() => {});
  }, [open, tags]);
  useEffect(() => {
    const h = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener("mousedown", h);
    return () => document.removeEventListener("mousedown", h);
  }, []);

  const toggle = (t) => setSel((s) => (s.includes(t) ? s.filter((x) => x !== t) : [...s, t]));
  const submit = async () => {
    setBusy(true); setMsg("");
    try {
      await crmApi.submitFeedback({
        lead_id: leadId, conversation_id: conversationId, tags: sel, comment,
        missed_services: missedServices.split(",").map((s) => s.trim()).filter(Boolean),
        estimated_missed_value: missedValue ? Number(missedValue) : null,
      });
      setMsg("Recorded ✓");
      setTimeout(() => { setOpen(false); setSel([]); setComment(""); setMissedServices(""); setMissedValue(""); setMsg(""); }, 900);
    } catch (e) { setMsg(e.message); }
    finally { setBusy(false); }
  };

  return (
    <span className="relative inline-block" ref={ref}>
      <button type="button" title="Rate this AI reply" data-testid={`reply-feedback-btn-${conversationId}`}
        onClick={() => setOpen((o) => !o)}
        className="text-[#7A6B5F] hover:text-[#6E1F2C] align-middle">
        <MessageSquareWarning size={11} />
      </button>
      {open && (
        <div className="absolute z-40 bottom-5 right-0 w-72 bg-white border border-[#6E1F2C]/25 shadow-xl p-2.5 space-y-1.5 text-left" data-testid="reply-feedback-popover">
          <p className="text-[9px] tracking-[0.16em] uppercase text-[#7A6B5F]">Reply feedback (internal — trains coaching)</p>
          <div className="max-h-44 overflow-y-auto grid grid-cols-1 gap-0.5">
            {Object.entries(tags).map(([k, label]) => (
              <label key={k} className="flex items-center gap-1.5 text-[10px] text-[#3E2A22] cursor-pointer">
                <input type="checkbox" className="accent-[#6E1F2C] w-3 h-3" checked={sel.includes(k)}
                  onChange={() => toggle(k)} data-testid={`fb-tag-${k}`} />
                {label}
              </label>))}
          </div>
          {sel.includes("missed_upsell") && (
            <div className="space-y-1 border-t border-[#6E1F2C]/10 pt-1" data-testid="fb-upsell-fields">
              <input className={inputCls} placeholder="Missed services (comma separated, e.g. flooring, TV wall)"
                value={missedServices} onChange={(e) => setMissedServices(e.target.value)} data-testid="fb-missed-services" />
              <input type="number" min="0" className={inputCls} placeholder="Estimated missed value (AED)"
                value={missedValue} onChange={(e) => setMissedValue(e.target.value)} data-testid="fb-missed-value" />
            </div>
          )}
          <textarea rows={2} className={inputCls} placeholder="Optional comment…" value={comment}
            onChange={(e) => setComment(e.target.value)} data-testid="fb-comment" />
          <div className="flex items-center gap-2">
            <button type="button" onClick={submit} disabled={busy || sel.length === 0} data-testid="fb-submit"
              className="bg-[#6E1F2C] text-white px-2.5 py-1 text-[9px] uppercase tracking-[0.12em] disabled:opacity-40">
              {busy ? "Saving…" : "Submit"}
            </button>
            {msg && <span className={`text-[9px] ${msg.includes("✓") ? "text-[#3F6E2C]" : "text-[#A3141A]"}`}>{msg}</span>}
          </div>
        </div>
      )}
    </span>
  );
}
